// src/components/work_orders/WorkOrderAiHelper.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — AI Work Order Helper (presentation; logic in workOrderAiGuide.js)
//
//   <WoHint field|metric />                 (?) plain-language explanation popover
//   <WoUrgencyBadge workOrder />            table / kanban cell: urgency + reasons
//   <WorkOrderAiHelper workOrder onAction /> full explainable panel (View modal)
//   <WorkOrderAiActionBar workOrders onAction />  page-level "what to do next"
//
// onAction(intent, payload) intents:
//   ACCEPT_SUGGESTION · ASSIGN · ESCALATE · START · FOLLOW_UP · COMPLETE · EDIT ·
//   CITIZEN_UPDATE · OPEN_LINK · FILTER (page bar)
// ─────────────────────────────────────────────────────────────────────────────
import React, { memo, useMemo } from 'react';
import {
    Badge, Box, Button, CircularProgress, CircularProgressLabel, Flex, HStack, Icon, IconButton,
    Popover, PopoverArrow, PopoverBody, PopoverContent, PopoverTrigger, Portal, Progress,
    SimpleGrid, Text, Tooltip, VStack, Wrap, WrapItem, useColorModeValue,
} from '@chakra-ui/react';
import {
    MdHelpOutline, MdOutlineAutoAwesome, MdPsychology, MdCheckCircle, MdErrorOutline, MdThumbUp,
    MdPersonAdd, MdPriorityHigh, MdPlayArrow, MdChat, MdDoneAll, MdEdit, MdEmail, MdOpenInNew,
    MdLink, MdPlace, MdTimer, MdPersonOff, MdWarning, MdHub,
    // --- UPDATE
    MdFactCheck, MdAddTask,
    // --- END UPDATE
} from 'react-icons/md';
import {
    WO_FIELD_GUIDE, WO_METRIC_GUIDE, WO_AI_MODELS, analyzeWorkOrder, getWorkOrderAnalysis, summarizeWorkOrders,
} from './workOrderAiGuide';
// --- UPDATE
import { inferDomain, inferType, getDomain, getType } from './workOrderCatalog';
// --- END UPDATE

const PRIORITY_SCHEME = { critical: 'red', high: 'orange', medium: 'yellow', low: 'gray' };
const INTENT_ICON = {
    ACCEPT_SUGGESTION: MdThumbUp, ASSIGN: MdPersonAdd, ESCALATE: MdPriorityHigh, START: MdPlayArrow,
    FOLLOW_UP: MdChat, COMPLETE: MdDoneAll, EDIT: MdEdit, CITIZEN_UPDATE: MdEmail, OPEN_LINK: MdOpenInNew,
    CREATE_INSPECTION: MdFactCheck, CREATE_FOLLOW_UP: MdAddTask, // --- UPDATE
};
const INTENT_BUTTON = {
    ACCEPT_SUGGESTION: 'Accept', ASSIGN: 'Assign', ESCALATE: 'Escalate', START: 'Start', FOLLOW_UP: 'Messages',
    COMPLETE: 'Complete', EDIT: 'Edit', CITIZEN_UPDATE: 'Draft', OPEN_LINK: 'Open',
    CREATE_INSPECTION: 'Inspection', CREATE_FOLLOW_UP: 'Follow-up', // --- UPDATE
};
const MODEL_STATE = { ready: 'green', partial: 'yellow', blocked: 'red' };

// ─────────────────────────────────────────────────────────────────────────────
export const WoHint = memo(({ field, metric, placement = 'top' }) => {
    const g = field ? WO_FIELD_GUIDE[field] : WO_METRIC_GUIDE[metric];
    const bg = useColorModeValue('white', 'gray.800');
    const muted = useColorModeValue('gray.600', 'gray.300');
    if (!g) return null;
    return (
        <Popover trigger="hover" placement={placement} isLazy openDelay={150}>
            <PopoverTrigger>
                <IconButton as="span" role="button" tabIndex={0} aria-label={`What is ${g.label}?`} icon={<Icon as={MdHelpOutline} />}
                    size="xs" variant="ghost" minW="auto" h="auto" p={0.5} ml={1} color="purple.400" verticalAlign="middle"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} />
            </PopoverTrigger>
            <Portal>
                <PopoverContent bg={bg} w="290px" borderRadius="xl" boxShadow="xl" textTransform="none" letterSpacing="normal" fontWeight="normal">
                    <PopoverArrow bg={bg} />
                    <PopoverBody p={3}>
                        <HStack spacing={1.5} mb={1}>
                            <Icon as={MdOutlineAutoAwesome} color="purple.400" />
                            <Text fontWeight="800" fontSize="sm">{g.label}</Text>
                        </HStack>
                        <Text fontSize="xs" color={muted} mb={g.why || g.scale ? 2 : 0}>{g.explain}</Text>
                        {g.why && <Text fontSize="xs" mb={2}><b>Why FlowSense needs it:</b> {g.why}</Text>}
                        {g.scale && <Text fontSize="xs"><b>Scale:</b> {g.scale}</Text>}
                        {g.usedBy?.length > 0 && (
                            <HStack spacing={1} mt={2} flexWrap="wrap">
                                {g.usedBy.map(k => <Badge key={k} colorScheme={WO_AI_MODELS[k].color} fontSize="9px">{WO_AI_MODELS[k].short}</Badge>)}
                            </HStack>
                        )}
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
});
WoHint.displayName = 'WoHint';

// ─────────────────────────────────────────────────────────────────────────────
// --- UPDATE: work order type + domain badge (inferred for older rows, marked "≈")
export const WoTypeBadge = memo(({ workOrder, showDomain = true }) => {
    const typeKey = inferType(workOrder || {});
    const domainKey = inferDomain(workOrder || {});
    const t = getType(typeKey);
    const d = getDomain(domainKey);
    if (!t && !d) return null;
    const inferred = !workOrder?.work_order_type;
    return (
        <Tooltip hasArrow placement="top" label={
            <Box fontSize="xs" maxW="240px">
                {t && <Text fontWeight="800">{t.label}</Text>}
                {t && <Text mb={1}>{t.explain}</Text>}
                {d && <Text>On: {d.label}{workOrder?.category ? ` · ${workOrder.category}` : ''}</Text>}
                {inferred && <Text mt={1} fontStyle="italic">Type inferred from the source / category — set it in Edit.</Text>}
            </Box>
        }>
            <HStack spacing={1} cursor="help" aria-label={`Type ${t ? t.short : 'unknown'}${d ? `, ${d.short}` : ''}`}>
                {t && <Badge colorScheme={t.color} fontSize="2xs" variant={inferred ? 'outline' : 'subtle'}>{inferred ? '≈ ' : ''}{t.short}</Badge>}
                {showDomain && d && <Badge colorScheme={d.color} fontSize="2xs" variant="outline">{d.short}</Badge>}
            </HStack>
        </Tooltip>
    );
});
WoTypeBadge.displayName = 'WoTypeBadge';
// --- END UPDATE

export const WoUrgencyBadge = memo(({ workOrder }) => {
    const a = getWorkOrderAnalysis(workOrder);
    if (a.closed) return <Badge colorScheme="gray" fontSize="2xs" variant="subtle">Done</Badge>;
    return (
        <Tooltip hasArrow placement="left" label={
            <Box fontSize="xs" maxW="240px">
                <Text fontWeight="800" mb={1}>AI urgency {a.score}/100 — {a.level.label}</Text>
                {a.drivers.slice(0, 4).map((d, i) => <Text key={i}>+{d.points} {d.label}</Text>)}
                {a.actions[0] && <Text mt={1} fontWeight="700">→ {a.actions[0].label}</Text>}
            </Box>
        }>
            <HStack spacing={1} cursor="help" aria-label={`AI urgency ${a.score}, ${a.level.label}`}>
                <Badge colorScheme={a.level.color} variant="solid" fontSize="2xs" minW="26px" textAlign="center">{a.score}</Badge>
                <Progress value={a.score} size="xs" w="40px" borderRadius="full" colorScheme={a.level.color} />
            </HStack>
        </Tooltip>
    );
});
WoUrgencyBadge.displayName = 'WoUrgencyBadge';

// ─────────────────────────────────────────────────────────────────────────────
/**
 * @param {object} workOrder
 * @param {(intent: string, action: object) => void} [onAction]
 * @param {number} [usedItemsCount]
 * @param {string} [busyIntent]  intent currently running (shows a spinner)
 */
export const WorkOrderAiHelper = memo(({ workOrder, onAction, usedItemsCount, busyIntent }) => {
    const a = useMemo(() => analyzeWorkOrder(workOrder || {}, { usedItemsCount }), [workOrder, usedItemsCount]);
    const shell = useColorModeValue('linear(135deg, purple.50, white 55%, blue.50)', 'linear(135deg, rgba(128,90,213,0.14), gray.800 55%, rgba(49,130,206,0.12))');
    const border = useColorModeValue('purple.100', 'whiteAlpha.200');
    const card = useColorModeValue('white', 'gray.800');
    const cardBorder = useColorModeValue('gray.200', 'gray.700');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const track = useColorModeValue('gray.100', 'whiteAlpha.200');
    if (!workOrder) return null;

    return (
        <Box bgGradient={shell} borderWidth="1px" borderColor={border} borderRadius="xl" p={4} role="region" aria-label="AI work order helper">
            {/* Header */}
            <Flex gap={4} align="center" wrap="wrap">
                <CircularProgress value={a.closed ? 100 : a.score} color={`${a.level.color}.400`} size="64px" thickness="10px" trackColor={track}>
                    <CircularProgressLabel fontSize="md" fontWeight="900">{a.closed ? '✓' : a.score}</CircularProgressLabel>
                </CircularProgress>
                <Box flex={1} minW="220px">
                    <HStack spacing={2} wrap="wrap">
                        <Icon as={MdPsychology} color="purple.400" />
                        <Text fontWeight="800">AI Work Order Assistant</Text>
                        <Badge colorScheme={a.level.color}>{a.closed ? 'Resolved' : `${a.level.label} urgency`}</Badge>
                        <WoHint metric="urgency" />
                    </HStack>
                    <Text fontSize="sm" mt={1}>{a.summary}</Text>
                </Box>
                <Box minW="150px">
                    <HStack spacing={0}><Text fontSize="2xs" fontWeight="800" textTransform="uppercase" color={muted}>Data readiness</Text></HStack>
                    <HStack spacing={2}>
                        <Progress value={a.readiness} size="sm" w="90px" borderRadius="full" colorScheme={a.readinessGrade.color} bg={track} />
                        <Text fontSize="sm" fontWeight="800">{a.readiness}%</Text>
                    </HStack>
                    <Text fontSize="2xs" color={muted}>{a.readinessGrade.label}</Text>
                </Box>
            </Flex>

            {/* Model strip */}
            <Wrap spacing={1.5} mt={3}>
                {a.models.map(m => (
                    <WrapItem key={m.key}>
                        <Tooltip hasArrow label={`${m.label}: ${m.state}. ${m.explain}${m.missing.length ? ` Missing: ${m.missing.map(c => c.label.toLowerCase()).join(', ')}.` : ''}`}>
                            <Badge colorScheme={MODEL_STATE[m.state]} variant="subtle" borderRadius="full" px={2} cursor="help" fontSize="10px">{m.short}</Badge>
                        </Tooltip>
                    </WrapItem>
                ))}
            </Wrap>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={4}>
                {/* Next actions */}
                <Box bg={card} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={3}>
                    <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={muted} mb={2}>Next best actions</Text>
                    <VStack align="stretch" spacing={2}>
                        {a.actions.length === 0 && <Text fontSize="xs" color={muted}>Nothing to do right now.</Text>}
                        {a.actions.map((act, i) => (
                            <Flex key={`${act.intent}-${i}`} gap={2} align="flex-start">
                                <Icon as={INTENT_ICON[act.intent] || MdOutlineAutoAwesome} color={`${PRIORITY_SCHEME[act.priority]}.400`} mt={0.5} />
                                <Box flex={1} minW={0}>
                                    <HStack spacing={1.5} wrap="wrap">
                                        <Text fontSize="xs" fontWeight="800">{act.label}</Text>
                                        <Badge colorScheme={PRIORITY_SCHEME[act.priority]} fontSize="9px">{act.priority}</Badge>
                                    </HStack>
                                    <Text fontSize="2xs" color={muted}>{act.explain}</Text>
                                </Box>
                                {onAction && (
                                    <Button size="xs" variant="outline" colorScheme="purple" flexShrink={0}
                                        isLoading={busyIntent === act.intent} onClick={() => onAction(act.intent, act)}>
                                        {INTENT_BUTTON[act.intent] || 'Do it'}
                                    </Button>
                                )}
                            </Flex>
                        ))}
                    </VStack>
                </Box>

                {/* Why */}
                <Box bg={card} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={3}>
                    <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={muted} mb={2}>{a.closed ? 'Outcome' : 'Why this urgency'}</Text>
                    {a.closed ? (
                        <Text fontSize="xs">SLA: <b>{a.sla.status.replace(/_/g, ' ')}</b>. {a.checks.find(c => c.id === 'resolution')?.status === 'ok' ? 'Resolution notes recorded.' : 'No resolution notes yet.'}</Text>
                    ) : (
                        <VStack align="stretch" spacing={1}>
                            {a.drivers.map((d, i) => (
                                <Flex key={i} justify="space-between" fontSize="xs" gap={2}>
                                    <Box minW={0}><Text fontWeight="700" noOfLines={1}>{d.label}</Text><Text fontSize="2xs" color={muted} noOfLines={1}>{d.detail}</Text></Box>
                                    <Text fontFamily="mono" color={muted}>+{d.points}</Text>
                                </Flex>
                            ))}
                        </VStack>
                    )}
                </Box>
            </SimpleGrid>

            {/* Links to FlowSense */}
            <Box mt={4}>
                <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={muted} mb={2}>Linked in FlowSense</Text>
                {a.links.length === 0 ? (
                    <HStack spacing={2} fontSize="xs" color="orange.400">
                        <Icon as={MdLink} />
                        <Text>Not linked to an asset, road or sensor — link it so the repair feeds the asset's history and the failure model.</Text>
                    </HStack>
                ) : (
                    <Wrap spacing={2}>
                        {a.links.map(l => (
                            <WrapItem key={`${l.kind}-${l.id}`}>
                                <Button size="xs" variant="outline" colorScheme="teal" leftIcon={<Icon as={l.kind === 'sensor' ? MdHub : MdPlace} />}
                                    rightIcon={<Icon as={MdOpenInNew} />} onClick={() => onAction?.('OPEN_LINK', { link: l })}>
                                    {l.label}
                                </Button>
                            </WrapItem>
                        ))}
                    </Wrap>
                )}
            </Box>

            {/* Data checklist */}
            <Box mt={4} bg={card} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={3}>
                <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={muted} mb={2}>What the AI needs on this job</Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={1.5}>
                    {a.checks.map(c => (
                        <HStack key={c.id} spacing={2} align="flex-start">
                            <Icon as={c.status === 'ok' ? MdCheckCircle : MdErrorOutline} color={c.status === 'ok' ? 'green.400' : c.level === 'required' ? 'red.400' : 'orange.400'} mt={0.5} />
                            <Box>
                                <HStack spacing={0}><Text fontSize="xs" fontWeight="700">{c.label}</Text><WoHint field={c.id} /></HStack>
                                {c.status !== 'ok' && <Text fontSize="2xs" color={muted}>{c.why}</Text>}
                            </Box>
                        </HStack>
                    ))}
                </SimpleGrid>
            </Box>
        </Box>
    );
});
WorkOrderAiHelper.displayName = 'WorkOrderAiHelper';

// ─────────────────────────────────────────────────────────────────────────────
/** Page-level "what to do next" bar. onAction('FILTER', { filter }) | onAction('ACCEPT_ALL') | onAction('VIEW', { workOrder }) */
export const WorkOrderAiActionBar = memo(({ workOrders, onAction, isBusy }) => {
    const s = useMemo(() => summarizeWorkOrders(workOrders), [workOrders]);
    const bg = useColorModeValue('linear(to-r, purple.50, blue.50)', 'linear(to-r, rgba(128,90,213,0.16), rgba(49,130,206,0.12))');
    const border = useColorModeValue('purple.100', 'whiteAlpha.200');
    const muted = useColorModeValue('gray.600', 'gray.400');
    const chips = [
        s.breached && { key: 'breached', label: `${s.breached} SLA breached`, color: 'red', icon: MdErrorOutline, filter: 'breached' },
        s.unassignedCritical && { key: 'uc', label: `${s.unassignedCritical} unassigned critical`, color: 'red', icon: MdPersonOff, filter: 'unassigned_critical' },
        s.atRisk && { key: 'risk', label: `${s.atRisk} SLA at risk`, color: 'orange', icon: MdTimer, filter: 'at_risk' },
        s.stagnant && { key: 'stag', label: `${s.stagnant} stagnant 24 h+`, color: 'yellow', icon: MdWarning, filter: 'stagnant' },
        s.noAsset && { key: 'link', label: `${s.noAsset} not linked to an asset`, color: 'teal', icon: MdLink, filter: 'no_asset' },
        s.closedNoNotes && { key: 'notes', label: `${s.closedNoNotes} closed without notes`, color: 'gray', icon: MdEdit, filter: 'no_notes' },
    ].filter(Boolean);
    if (!s.open && !chips.length) return null;
    return (
        <Box bgGradient={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4} role="region" aria-label="AI action bar">
            <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
                <HStack spacing={2}>
                    <Icon as={MdPsychology} color="purple.400" boxSize={5} />
                    <Box>
                        <Text fontWeight="800" fontSize="sm">AI dispatch assistant <WoHint metric="urgency" /></Text>
                        <Text fontSize="xs" color={muted}>{s.open} open on this page · {s.critical} critical urgency. Click a chip to filter; sort by "AI Urgency" to work top-down.</Text>
                    </Box>
                </HStack>
                {s.suggestions > 0 && onAction && (
                    <Button size="sm" colorScheme="purple" leftIcon={<Icon as={MdThumbUp} />} onClick={() => onAction('ACCEPT_ALL')} isLoading={isBusy}>
                        Accept {s.suggestions} AI suggestion{s.suggestions === 1 ? '' : 's'}
                    </Button>
                )}
            </Flex>
            {chips.length > 0 && (
                <Wrap spacing={2} mt={3}>
                    {chips.map(c => (
                        <WrapItem key={c.key}>
                            <Button size="xs" colorScheme={c.color} variant="outline" borderRadius="full" leftIcon={<Icon as={c.icon} />}
                                onClick={() => onAction?.('FILTER', { filter: c.filter })}>
                                {c.label}
                            </Button>
                        </WrapItem>
                    ))}
                </Wrap>
            )}
            {s.top.length > 0 && (
                <Box mt={3}>
                    <Text fontSize="2xs" fontWeight="800" textTransform="uppercase" color={muted} mb={1}>Do these first</Text>
                    <Wrap spacing={2}>
                        {s.top.slice(0, 3).map(({ wo, a }) => (
                            <WrapItem key={wo.id}>
                                <Button size="xs" variant="ghost" onClick={() => onAction?.('VIEW', { workOrder: wo })} aria-label={`Open ${wo.title}`}>
                                    <Badge colorScheme={a.level.color} variant="solid" mr={1.5}>{a.score}</Badge>
                                    <Text as="span" noOfLines={1} maxW="220px">{wo.title}</Text>
                                </Button>
                            </WrapItem>
                        ))}
                    </Wrap>
                </Box>
            )}
        </Box>
    );
});
WorkOrderAiActionBar.displayName = 'WorkOrderAiActionBar';

export default WorkOrderAiHelper;